import React, { useMemo } from 'react';

// ── Inline formatter (bold, italic, inline-code) ──────────────────────────────
const escHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inlineFormat = (s) =>
  escHtml(s)
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="md-code-inline">$1</code>');

// ── Table detector / parser ───────────────────────────────────────────────────
const isTableRow  = (line) => /^\s*\|/.test(line);
const isSeparator = (line) => /^\s*\|[\s\-:|]+\|\s*$/.test(line);

function parseTableBlock(tableLines) {
  // tableLines: array of raw pipe-delimited strings
  const rows = tableLines.map((line) =>
    line
      .replace(/^\s*\|/, '')   // strip leading pipe
      .replace(/\|\s*$/, '')   // strip trailing pipe
      .split('|')
      .map((cell) => cell.trim())
  );

  if (rows.length < 2) return null;

  const headers   = rows[0];
  const bodyRows  = rows.slice(2); // skip separator row at index 1

  const ths = headers.map((h) => `<th class="md-th">${inlineFormat(h)}</th>`).join('');
  const trs = bodyRows
    .map((row) => {
      const tds = headers.map((_, i) =>
        `<td class="md-td">${inlineFormat(row[i] ?? '')}</td>`
      ).join('');
      return `<tr class="md-tr">${tds}</tr>`;
    })
    .join('');

  return `<div class="md-table-wrap"><table class="md-table"><thead><tr class="md-tr md-thead-row">${ths}</tr></thead><tbody>${trs}</tbody></table></div>`;
}

// ── Blockquote helper ─────────────────────────────────────────────────────────
const parseBlockquote = (text) =>
  `<blockquote class="md-blockquote">${inlineFormat(text)}</blockquote>`;

// ── Main parser ───────────────────────────────────────────────────────────────
function parseMarkdown(raw) {
  if (!raw || typeof raw !== 'string') return '';

  const lines     = raw.split('\n');
  const output    = [];
  let i           = 0;
  let listStack   = []; // stack of 'ul' | 'ol'

  const flushLists = () => {
    while (listStack.length) output.push(`</${listStack.pop()}>`);
  };

  while (i < lines.length) {
    const line = lines[i];

    // ── Fenced code block ───────────────────────────────────────────────────
    if (/^```/.test(line)) {
      flushLists();
      const lang       = line.slice(3).trim();
      const codeLines  = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(escHtml(lines[i]));
        i++;
      }
      output.push(
        `<pre class="md-pre"><code class="md-code-block${lang ? ` language-${lang}` : ''}">${codeLines.join('\n')}</code></pre>`
      );
      i++;
      continue;
    }

    // ── Table block ─────────────────────────────────────────────────────────
    if (isTableRow(line)) {
      flushLists();
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      const tableHtml = parseTableBlock(tableLines);
      if (tableHtml) output.push(tableHtml);
      continue;
    }

    // ── Blockquote ──────────────────────────────────────────────────────────
    if (/^>\s?/.test(line)) {
      flushLists();
      output.push(parseBlockquote(line.replace(/^>\s?/, '')));
      i++;
      continue;
    }

    // ── Headings ────────────────────────────────────────────────────────────
    const h4 = line.match(/^#### (.+)/);
    const h3 = line.match(/^### (.+)/);
    const h2 = line.match(/^## (.+)/);
    const h1 = line.match(/^# (.+)/);
    if (h1 || h2 || h3 || h4) {
      flushLists();
      const lvl  = h1 ? 1 : h2 ? 2 : h3 ? 3 : 4;
      const text = inlineFormat((h1 || h2 || h3 || h4)[1]);
      output.push(`<h${lvl} class="md-h${lvl}">${text}</h${lvl}>`);
      i++;
      continue;
    }

    // ── Horizontal rule ─────────────────────────────────────────────────────
    if (/^---+$/.test(line.trim())) {
      flushLists();
      output.push('<hr class="md-hr" />');
      i++;
      continue;
    }

    // ── Unordered list item ─────────────────────────────────────────────────
    const ulMatch = line.match(/^(\s*)[*\-+] (.+)/);
    if (ulMatch) {
      if (!listStack.length || listStack[listStack.length - 1] !== 'ul') {
        flushLists();
        listStack.push('ul');
        output.push('<ul class="md-ul">');
      }
      output.push(`<li class="md-li">${inlineFormat(ulMatch[2])}</li>`);
      i++;
      continue;
    }

    // ── Ordered list item ───────────────────────────────────────────────────
    const olMatch = line.match(/^\d+\. (.+)/);
    if (olMatch) {
      if (!listStack.length || listStack[listStack.length - 1] !== 'ol') {
        flushLists();
        listStack.push('ol');
        output.push('<ol class="md-ol">');
      }
      output.push(`<li class="md-li">${inlineFormat(olMatch[1])}</li>`);
      i++;
      continue;
    }

    // ── Blank line ──────────────────────────────────────────────────────────
    if (line.trim() === '') {
      flushLists();
      output.push('<div class="md-spacer"></div>');
      i++;
      continue;
    }

    // ── Regular paragraph ───────────────────────────────────────────────────
    flushLists();
    output.push(`<p class="md-p">${inlineFormat(line)}</p>`);
    i++;
  }

  flushLists();
  return output.join('\n');
}

// ── Styles ────────────────────────────────────────────────────────────────────
const MD_STYLES = `
  .md-root { font-size: 14px; line-height: 1.75; color: #1a1a2e; }

  .md-h1 { font-size: 1.35rem; font-weight: 700; margin: 1.25rem 0 0.5rem; color: #0A0A10; }
  .md-h2 { font-size: 1.1rem; font-weight: 700; margin: 1.1rem 0 0.4rem; color: #0A0A10;
            border-bottom: 1px solid rgba(215,180,114,0.4); padding-bottom: 0.3rem; }
  .md-h3 { font-size: 0.97rem; font-weight: 600; margin: 0.9rem 0 0.3rem; color: #0A0A10; }
  .md-h4 { font-size: 0.9rem;  font-weight: 600; margin: 0.7rem 0 0.25rem; color: #0A0A10; }

  .md-p   { margin: 0.25rem 0; }

  .md-ul, .md-ol { margin: 0.35rem 0 0.35rem 1.4rem; }
  .md-li  { margin: 0.2rem 0; }

  .md-code-inline { background: rgba(215,180,114,0.18); border-radius: 3px;
                    padding: 0.1em 0.4em; font-size: 0.83em; font-family: monospace; color: #8B6914; }
  .md-pre  { background: #1e1e2e; border-radius: 8px; padding: 1rem;
             overflow-x: auto; margin: 0.75rem 0; }
  .md-code-block { font-family: monospace; font-size: 0.82rem; color: #cdd6f4; white-space: pre; }

  .md-hr   { border: none; border-top: 1px solid rgba(215,180,114,0.4); margin: 1rem 0; }
  .md-spacer { height: 0.4rem; }

  .md-blockquote {
    border-left: 3px solid #D7B472;
    margin: 0.6rem 0;
    padding: 0.4rem 0.85rem;
    background: rgba(215,180,114,0.07);
    border-radius: 0 6px 6px 0;
    color: #4a4a5a;
    font-style: italic;
  }

  /* ── Tables ── */
  .md-table-wrap {
    overflow-x: auto;
    margin: 0.75rem 0;
    border-radius: 8px;
    border: 1px solid rgba(215,180,114,0.4);
  }
  .md-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
  }
  .md-thead-row {
    background-color: rgba(215,180,114,0.12);
  }
  .md-th {
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #0A0A10;
    border-bottom: 1px solid rgba(215,180,114,0.4);
    white-space: nowrap;
  }
  .md-tr:nth-child(even) { background-color: rgba(215,180,114,0.04); }
  .md-tr:hover           { background-color: rgba(215,180,114,0.08); }
  .md-td {
    padding: 7px 12px;
    color: #2a2a3e;
    border-bottom: 1px solid rgba(215,180,114,0.15);
    vertical-align: top;
  }

  strong { font-weight: 700; }
  em     { font-style: italic; }
`;

/**
 * Props:
 *   content   {string}  – raw markdown string
 *   className {string}  – optional extra CSS classes
 */
export default function MarkdownPreview({ content, className = '' }) {
  const html = useMemo(() => parseMarkdown(content), [content]);

  return (
    <>
      <style>{MD_STYLES}</style>
      <div
        className={`md-root ${className}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}
